import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import {
  Plus,
  Search,
  MapPin,
  Trash2,
  Zap,
  History,
  RotateCcw,
  XCircle,
  Clock,
  Tag,
  Calendar,
} from "lucide-react";
import { format } from "date-fns";

import Loader from "../../../loader/Loader";
import AddSellingPrice from "./AddSellingPrice.jsx";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Pagination from "../../Pagination";
import { domain } from "../../../../security";
import {
  selectUserID,
  selectPriceTypeConfig,
  SET_PriceTypeConfig,
} from "../../../../redux/IchthusSlice";

const formatDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "-");

// ------------------------------------------------------------
// Deleted History Modal (unchanged)
// ------------------------------------------------------------
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${domain}/api/SellingPriceHistories/history?search=${debouncedSearch}`,
      );
      setHistoryData(res.data);
    } catch {
      toast.error("Failed to load selling price history.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = historyData.slice(
    indexOfLast - itemsPerPage,
    indexOfLast,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Selling Price History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by product or location..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted selling prices found.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Product</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">UOM</th>
                      <th className="px-4 py-3">SRP (VAT Inc)</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1">
                          {item.productName}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="text-amber-500 hover:text-amber-700 ml-1"
                            title="View Audit Info"
                          >
                            <Clock size={14} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {item.locationName}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-indigo-600">
                          {item.uom}
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-slate-800">
                          ₱{Number(item.vatInc).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {item.endDate ? (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-semibold">
                              Promo
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">
                              Standard
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className="font-medium text-red-500">
                            {item.deletedByFullName || "Unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile stack */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-800">
                          {item.productName}
                        </h4>
                        <span className="text-xs text-slate-400">
                          {item.locationName} · {item.uom}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100 transition"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-slate-500">
                      <span className="font-bold text-slate-800">
                        ₱{Number(item.vatInc).toLocaleString()} SRP
                      </span>
                      <span className="flex items-center gap-1">
                        <Trash2 size={10} className="text-red-400" />
                        Deleted by:{" "}
                        <span className="font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={10} className="text-slate-400" />
                        {formatDate(item.deletedDate)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`${auditItem?.productName} — ${auditItem?.uom}`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ------------------------------------------------------------
// MAIN COMPONENT
// ------------------------------------------------------------
const AllSellingPriceHistories = () => {
  const currentUserID = useSelector(selectUserID);
  const priceTypeConfig = useSelector(selectPriceTypeConfig);
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [productUomsMap, setProductUomsMap] = useState({});
  const [modalConfig, setModalConfig] = useState({
    itemToEdit: null,
    prefillData: null,
  });
  const [auditItem, setAuditItem] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Locations with IDs for config fetching
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocationName, setSelectedLocationName] = useState("All");

  const fetchedUomIdsRef = useRef(new Set());

  // ------------------------------------------------------------
  // Fetch locations from API (once)
  // ------------------------------------------------------------
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get(`${domain}/api/Locations`);
        setLocations(res.data);
        /* if (res.data.length > 0) {
          const firstLoc = res.data[0];
          setSelectedLocationId(firstLoc.id);
          setSelectedLocationName(firstLoc.locationName);
        } */
      } catch {
        toast.error("Failed to load locations.");
      }
    };
    fetchLocations();
  }, []);

  // ------------------------------------------------------------
  // Fetch price configuration when location changes
  // ------------------------------------------------------------
  const fetchPriceConfig = useCallback(
    async (locationId) => {
      if (!locationId) return;
      try {
        const res = await axios.get(
          `${domain}/api/PersonalizedPricing?locationId=${locationId}`,
        );
        const configs = res.data;
        let matched = null;

        if (Array.isArray(configs) && configs.length > 0) {
          matched =
            configs.find((c) => c.locationId === locationId) ||
            configs.find((c) => !c.locationId) ||
            configs[0];
        }

        if (matched) {
          dispatch(
            SET_PriceTypeConfig({
              showVatInc: matched.showVatInc,
              showVatEx: matched.showVatEx,
              showReseller: matched.showReseller,
              showZeroRated: matched.showZeroRated,
              configName: matched.configName,
            }),
          );
        } else {
          // fallback: global config
          const globalRes = await axios.get(
            `${domain}/api/PersonalizedPricing`,
          );
          const globalConfigs = globalRes.data;
          const globalMatch = Array.isArray(globalConfigs)
            ? globalConfigs.find((c) => !c.locationId) || globalConfigs[0]
            : null;
          if (globalMatch) {
            dispatch(
              SET_PriceTypeConfig({
                showVatInc: globalMatch.showVatInc,
                showVatEx: globalMatch.showVatEx,
                showReseller: globalMatch.showReseller,
                showZeroRated: globalMatch.showZeroRated,
                configName: globalMatch.configName,
              }),
            );
          }
        }
      } catch {
        // silent fail
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedLocationId) {
      fetchPriceConfig(selectedLocationId);
    }
  }, [selectedLocationId, fetchPriceConfig]);

  // ------------------------------------------------------------
  // Fetch main selling price data
  // ------------------------------------------------------------
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/SellingPriceHistories`);
      setData(response.data);
    } catch {
      toast.error("Failed to fetch selling prices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ------------------------------------------------------------
  // Group data by product+location
  // ------------------------------------------------------------
  const groupedData = useMemo(() => {
    const groups = {};
    data.forEach((item) => {
      const key = `${item.productId}-${item.locationId}`;
      if (!groups[key]) {
        groups[key] = {
          uniqueKey: key,
          productId: item.productId,
          productName: item.productName,
          locationId: item.locationId,
          locationName: item.locationName,
          notes: item.notes,
          standardPrices: {},
          promos: [],
          _auditSample: item,
        };
      }
      if (item.endDate) groups[key].promos.push(item);
      else groups[key].standardPrices[item.uom] = item;
    });
    return Object.values(groups);
  }, [data]);

  // ------------------------------------------------------------
  // Filter groups by search term and selected location name
  // ------------------------------------------------------------
  const filteredGroups = useMemo(() => {
    return groupedData.filter((group) => {
      const matchesSearch =
        (group.productName || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) &&
        (selectedLocationName === "All" ||
          group.locationName === selectedLocationName);
      return matchesSearch;
    });
  }, [groupedData, searchTerm, selectedLocationName]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLocationName]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredGroups.slice(
    indexOfLastItem - itemsPerPage,
    indexOfLastItem,
  );

  // ------------------------------------------------------------
  // Lazy load UOMs
  // ------------------------------------------------------------
  useEffect(() => {
    if (currentItems.length === 0) return;

    const visibleIds = [
      ...new Set(currentItems.map((item) => item.productId)),
    ].filter((id) => !fetchedUomIdsRef.current.has(id));

    if (visibleIds.length === 0) return;

    visibleIds.forEach((id) => fetchedUomIdsRef.current.add(id));

    const fetchUoms = async () => {
      const results = await Promise.all(
        visibleIds.map(async (id) => {
          try {
            const res = await axios.get(
              `${domain}/api/Products/${id}/pricing-uoms`,
            );
            return { id, uoms: res.data };
          } catch {
            fetchedUomIdsRef.current.delete(id);
            return { id, uoms: [] };
          }
        }),
      );
      setProductUomsMap((prev) => ({
        ...prev,
        ...Object.fromEntries(results.map((r) => [r.id, r.uoms])),
      }));
    };

    fetchUoms();
  }, [currentItems]);

  // ------------------------------------------------------------
  // Soft delete & restore
  // ------------------------------------------------------------
  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this selling price?"))
      return;
    try {
      await axios.put(
        `${domain}/api/SellingPriceHistories/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Selling price moved to history!");
      fetchData();
    } catch {
      toast.error("Failed to delete selling price.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/SellingPriceHistories/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Selling price restored successfully!");
      fetchData();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to restore selling price.";
      toast.error(message);
    }
  };

  // ------------------------------------------------------------
  // Modal helpers
  // ------------------------------------------------------------
  const openEdit = (item) => {
    setModalConfig({ itemToEdit: { ...item }, prefillData: null });
    setIsModalVisible(true);
  };

  const openAdd = (productId, locationId, uomCode, isSpecial = false) => {
    const prefillData = productId
      ? { productId, locationId, prefillUom: uomCode, isSpecial }
      : null;
    setModalConfig({ itemToEdit: null, prefillData });
    setIsModalVisible(true);
  };

  const handleLocationChange = (e) => {
    const locId = parseInt(e.target.value, 10);
    const loc = locations.find((l) => l.id === locId);
    if (loc) {
      setSelectedLocationId(loc.id);
      setSelectedLocationName(loc.locationName);
    } else {
      // "All" option
      setSelectedLocationId(null);
      setSelectedLocationName("All");
    }
  };

  // ------------------------------------------------------------
  // Helper to get active price fields (same as in AddSellingPrice)
  // ------------------------------------------------------------
  const activePriceFields = useMemo(() => {
    const ALL_PRICE_FIELDS = [
      { configKey: "showVatInc", field: "vatInc", label: "VAT Inc" },
      { configKey: "showVatEx", field: "vatEx", label: "VAT Ex" },
      { configKey: "showReseller", field: "reseller", label: "Reseller" },
    ];
    return ALL_PRICE_FIELDS.filter((pf) => priceTypeConfig[pf.configKey]);
  }, [priceTypeConfig]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(value ?? 0);

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-100/40 pb-12 font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-white to-slate-50/50 border-b border-slate-100 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2.5 tracking-tight">
                <div className="p-2.5 rounded-lg bg-emerald-100/60 text-emerald-600">
                  <Tag size={20} />
                </div>
                Selling Price Management
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                Manage standard pricing and promotional offers per branch.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 text-sm font-medium px-4 py-2.5 rounded-lg transition-all duration-200"
              >
                <History size={17} />
                <span>History</span>
              </button>
              <button
                onClick={() => openAdd(null, null, null)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all active:scale-95 duration-200"
              >
                <Plus size={18} /> Add New Price
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Filters */}
        <div className="bg-white border border-slate-100 p-4 rounded-xl shadow-sm grid md:grid-cols-3 gap-4 mb-6">
          <div className="relative md:col-span-2">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Product Name..."
              className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:shadow-sm transition-all text-sm text-slate-700 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              value={selectedLocationId || ""}
              onChange={handleLocationChange}
              className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:shadow-sm transition-all text-sm appearance-none bg-white font-medium text-slate-700 outline-none"
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.locationName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <Loader />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 text-xs uppercase text-slate-500 font-semibold">
                <tr>
                  <th className="px-6 py-4 w-1/4 font-semibold tracking-wide">
                    Product & Location
                  </th>
                  <th className="px-6 py-4 w-3/4 font-semibold tracking-wide">
                    Pricing Configuration
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {currentItems.length > 0 ? (
                  currentItems.map((group) => {
                    const allowedUomObjects = (
                      productUomsMap[group.productId] || []
                    ).sort((a, b) =>
                      (a.code || "").localeCompare(b.code || ""),
                    );
                    return (
                      <tr
                        key={group.uniqueKey}
                        className="hover:bg-emerald-50/30 transition-colors duration-150"
                      >
                        <td className="px-6 py-6 align-top">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 text-base">
                              {group.productName}
                            </span>
                            <span className="flex items-center gap-1.5 text-slate-400 text-xs mt-1.5">
                              <MapPin size={13} /> {group.locationName}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-5 align-top">
                          <div className="flex flex-col gap-6">
                            {/* --- Standard Prices Section --- */}
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                  Standard Pricing
                                </h4>
                                <div className="h-px bg-slate-100 flex-1"></div>
                              </div>

                              <div className="flex flex-wrap items-stretch gap-3">
                                {allowedUomObjects.map((uomObj) => {
                                  const priceItem =
                                    group.standardPrices[uomObj.code];

                                  return priceItem ? (
                                    <div
                                      key={uomObj.code}
                                      className="relative group/card flex"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => openEdit(priceItem)}
                                        className="flex flex-col text-left min-w-[140px] p-3.5 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-emerald-400 hover:ring-1 hover:ring-emerald-400 hover:shadow-md transition-all duration-200"
                                      >
                                        {/* UOM Badge */}
                                        <span className="inline-flex px-2 py-0.5 mb-2 bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-bold uppercase tracking-wider rounded">
                                          {uomObj.name || uomObj.code}
                                        </span>

                                        {/* Price Fields Loop */}
                                        <div className="space-y-1.5 w-full mt-1">
                                          {activePriceFields.map((pf) => (
                                            <div
                                              key={pf.field}
                                              className="flex justify-between items-center gap-4"
                                            >
                                              <span className="text-[11px] font-medium text-slate-400">
                                                {pf.label}
                                              </span>
                                              <span className="text-sm font-bold text-slate-800">
                                                {formatCurrency(
                                                  priceItem[pf.field],
                                                )}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </button>

                                      {/* Floating Action Buttons */}
                                      <div className="absolute -top-2.5 -right-2.5 flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity duration-200 z-10">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setAuditItem(priceItem);
                                          }}
                                          className="bg-amber-100 text-amber-600 rounded-full p-1.5 shadow-sm hover:shadow hover:bg-amber-200 transition-colors"
                                          title="Audit Info"
                                        >
                                          <Clock size={12} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSoftDelete(priceItem.id);
                                          }}
                                          className="bg-red-100 text-red-600 rounded-full p-1.5 shadow-sm hover:shadow hover:bg-red-200 transition-colors"
                                          title="Delete"
                                        >
                                          <Trash2 size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      key={uomObj.code}
                                      type="button"
                                      onClick={() =>
                                        openAdd(
                                          group.productId,
                                          group.locationId,
                                          uomObj.code,
                                        )
                                      }
                                      className="flex flex-col items-center justify-center min-w-[140px] p-3.5 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all duration-200 gap-1.5 group"
                                    >
                                      <div className="bg-slate-50 p-1.5 rounded-md group-hover:bg-emerald-100/50 transition-colors">
                                        <Plus size={16} />
                                      </div>
                                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 group-hover:text-emerald-600">
                                        Add {uomObj.name || uomObj.code}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* --- Promos Section --- */}
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <h4 className="text-xs font-bold text-orange-500 uppercase tracking-widest flex items-center gap-1.5">
                                  <Zap size={14} className="fill-orange-500" />{" "}
                                  Promos
                                </h4>
                                <div className="h-px bg-slate-100 flex-1"></div>
                              </div>

                              <div className="flex flex-wrap items-stretch gap-3">
                                {group.promos.map((promo) => (
                                  <div
                                    key={promo.id}
                                    className="relative group/promo flex"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openEdit(promo)}
                                      className="flex flex-col text-left min-w-[160px] p-3.5 bg-orange-50/50 border border-orange-200/80 rounded-xl shadow-sm hover:border-orange-400 hover:ring-1 hover:ring-orange-400 hover:bg-orange-50 transition-all duration-200"
                                    >
                                      {/* Promo Header */}
                                      <div className="flex justify-between items-start gap-4 mb-2">
                                        <span className="inline-flex px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-200 text-[10px] font-bold uppercase tracking-wider rounded">
                                          {promo.uom}
                                        </span>
                                        <span className="font-bold text-slate-800 text-sm">
                                          {formatCurrency(promo.vatInc)}
                                        </span>
                                      </div>

                                      {/* Price Fields Loop */}
                                      <div className="space-y-1.5 w-full mt-1 mb-3">
                                        {activePriceFields.map((pf) => (
                                          <div
                                            key={pf.field}
                                            className="flex justify-between items-center gap-4"
                                          >
                                            <span className="text-[11px] font-medium text-slate-500">
                                              {pf.label}
                                            </span>
                                            <span className="text-sm font-semibold text-slate-800">
                                              {formatCurrency(promo[pf.field])}
                                            </span>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Expiration Date Footer */}
                                      <div className="mt-auto pt-2 border-t border-orange-200/60 flex items-center gap-1.5 text-[11px] text-orange-600 font-medium">
                                        <Calendar size={12} />
                                        <span>
                                          Ends{" "}
                                          {new Date(
                                            promo.endDate,
                                          ).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </button>

                                    {/* Floating Action Buttons */}
                                    <div className="absolute -top-2.5 -right-2.5 flex items-center gap-1 opacity-0 group-hover/promo:opacity-100 transition-opacity duration-200 z-10">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAuditItem(promo);
                                        }}
                                        className="bg-amber-100 text-amber-600 rounded-full p-1.5 shadow-sm hover:shadow hover:bg-amber-200 transition-colors"
                                        title="Audit Info"
                                      >
                                        <Clock size={12} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSoftDelete(promo.id);
                                        }}
                                        className="bg-red-100 text-red-600 rounded-full p-1.5 shadow-sm hover:shadow hover:bg-red-200 transition-colors"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </div>
                                ))}

                                {/* Add Promo Button */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    openAdd(
                                      group.productId,
                                      group.locationId,
                                      "SPECIAL",
                                      true,
                                    )
                                  }
                                  className="flex flex-col items-center justify-center min-w-[140px] p-3.5 border-2 border-dashed border-orange-200 rounded-xl text-orange-400 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50/50 transition-all duration-200 gap-1.5 group"
                                >
                                  <div className="bg-orange-50 p-1.5 rounded-md group-hover:bg-orange-100 transition-colors">
                                    <Plus size={16} />
                                  </div>
                                  <span className="text-[11px] font-semibold uppercase tracking-wider text-orange-500 group-hover:text-orange-600">
                                    Add Promo
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="2"
                      className="text-center py-12 text-slate-400"
                    >
                      No selling prices found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="px-6 py-4 border-t border-slate-100 bg-gradient-to-r from-slate-50/50 to-white">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredGroups.length}
                currentPage={currentPage}
                paginate={setCurrentPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <AddSellingPrice
              onClose={() => {
                setIsModalVisible(false);
                fetchData();
              }}
              refreshData={fetchData}
              itemToEdit={modalConfig.itemToEdit}
              prefillData={modalConfig.prefillData}
            />
          </div>
        </div>
      )}

      {/* History Modal */}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* Audit Modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`${auditItem?.productName} — ${auditItem?.uom}`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllSellingPriceHistories;
